import { NavLink } from "react-router-dom";
import { useAuthStore } from "../../stores/auth";
import { useTenantStore } from "../../stores/tenant";

type NavItem = { to: string; label: string; icon: string; end?: boolean };
type NavGroup = { title: string; items: NavItem[]; adminOnly?: boolean };

// L'ordre reflète la priorité d'usage demandée par le client : le CRM est la
// première chose qu'il ouvre après le tableau de bord, il remonte donc juste
// en dessous. Les entrées d'appel redescendent dans « Activité ».
const GROUPS: NavGroup[] = [
  {
    title: "Principal",
    items: [{ to: "/dashboard", label: "Tableau de bord", icon: "grid", end: true }],
  },
  {
    title: "CRM",
    adminOnly: true,
    items: [
      { to: "/crm", label: "Pipeline", icon: "kanban", end: true },
      { to: "/crm/agenda", label: "Suivis & RDV", icon: "checklist", end: true },
      { to: "/crm/revenus", label: "Revenus", icon: "coins", end: true },
    ],
  },
  {
    // Supervision des SaaS clients (Protecto…) : leurs conversations, relues
    // depuis leur portail, prod et préprod séparées.
    title: "Supervision",
    adminOnly: true,
    items: [
      { to: "/supervision", label: "Conversations clients", icon: "radar", end: true },
      { to: "/supervision/sources", label: "Sources", icon: "plug", end: true },
    ],
  },
  {
    title: "Activité",
    items: [
      { to: "/calls/live", label: "Appels en direct", icon: "wave", end: true },
      { to: "/calls", label: "Historique appels", icon: "clock", end: true },
      { to: "/bookings", label: "Réservations", icon: "calendar", end: true },
      { to: "/callbacks", label: "Rappels à faire", icon: "phone", end: true },
      { to: "/kb", label: "Infos / FAQ", icon: "book", end: true },
      { to: "/forwards", label: "Redirections", icon: "arrow", end: true },
    ],
  },
  {
    title: "Système",
    adminOnly: true,
    items: [
      { to: "/tenants", label: "Établissements", icon: "building", end: true },
      { to: "/numbers", label: "Numéros", icon: "hash", end: true },
      { to: "/dev/voice-simulator", label: "Simulateur vocal", icon: "beaker", end: true },
    ],
  },
];

export function Sidebar() {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const currentTenantId = useTenantStore((s) => s.currentTenantId);
  const isAdmin = user?.role === "admin";

  // Interface client : la configuration de SON établissement (voix, accueil,
  // Google Calendar…) est accessible directement, sans passer par la liste
  // admin des établissements.
  const groups: NavGroup[] = [
    ...GROUPS.filter((g) => !g.adminOnly || isAdmin),
    ...(!isAdmin && currentTenantId
      ? [
          {
            title: "Mon établissement",
            items: [
              { to: `/tenants/${currentTenantId}/settings`, label: "Configuration", icon: "gear", end: true },
            ],
          },
        ]
      : []),
  ];

  return (
    <nav className="sidebar">
      <div className="sidebar__brand">
        {import.meta.env.VITE_BRAND === "umbeli" ? (
          <span className="sidebar__brand-mark sidebar__brand-mark--umbeli" aria-hidden="true">
            U
          </span>
        ) : (
          <span className="sidebar__brand-mark" aria-hidden="true">
            <img src="/logo_mark.png" alt="" width="34" height="34" />
          </span>
        )}
        <span className="sidebar__brand-text">
          {import.meta.env.VITE_BRAND === "umbeli" ? "Umbeli" : "Voice Responder"}
          <span className="sidebar__brand-sub">
            {import.meta.env.VITE_BRAND === "umbeli" ? "Agents vocaux IA" : "by Umbeli"}
          </span>
        </span>
      </div>

      <div className="sidebar__nav">
        {groups.map((group) => (
          <div key={group.title} className="sidebar__group">
            <div className="sidebar__group-title">{group.title}</div>
            {group.items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) => `sidebar__link${isActive ? " is-active" : ""}`}
              >
                <span className="sidebar__link-icon" aria-hidden="true">
                  <Icon name={item.icon} />
                </span>
                <span className="sidebar__link-label">{item.label}</span>
              </NavLink>
            ))}
          </div>
        ))}
      </div>

      <div className="sidebar__foot">
        <div className="sidebar__account">
          <span className="sidebar__account-avatar" aria-hidden="true">
            {(user?.email ?? "?").charAt(0).toUpperCase()}
          </span>
          <span className="sidebar__account-info">
            <span className="sidebar__account-name">{user?.email ?? "—"}</span>
            <span className="sidebar__account-role">{isAdmin ? "Administrateur" : "Établissement"}</span>
          </span>
        </div>
        <button type="button" className="sidebar__logout" onClick={() => logout()}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.9"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <path d="M16 17l5-5-5-5" />
            <path d="M21 12H9" />
          </svg>
          <span>Déconnexion</span>
        </button>
      </div>
    </nav>
  );
}

// Icônes SVG inline (trait fin, style Maple & Coast).
function Icon({ name }: { name: string }) {
  const common = {
    width: 18,
    height: 18,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };
  switch (name) {
    case "grid":
      return (
        <svg {...common}>
          <rect x="3" y="3" width="7" height="7" rx="1.5" />
          <rect x="14" y="3" width="7" height="7" rx="1.5" />
          <rect x="3" y="14" width="7" height="7" rx="1.5" />
          <rect x="14" y="14" width="7" height="7" rx="1.5" />
        </svg>
      );
    case "wave":
      return (
        <svg {...common}>
          <path d="M3 12h2l2-6 3 14 3-18 3 12 2-4h3" />
        </svg>
      );
    case "clock":
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v5l3 2" />
        </svg>
      );
    case "calendar":
      return (
        <svg {...common}>
          <rect x="3" y="5" width="18" height="16" rx="2" />
          <path d="M3 9h18M8 3v4M16 3v4" />
        </svg>
      );
    case "phone":
      return (
        <svg {...common}>
          <path d="M4 4h4l2 5-2.5 1.5a11 11 0 0 0 6 6L15 14l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 2 6a2 2 0 0 1 2-2z" />
        </svg>
      );
    case "book":
      return (
        <svg {...common}>
          <path d="M4 5a2 2 0 0 1 2-2h13v16H6a2 2 0 0 0-2 2z" />
          <path d="M4 19a2 2 0 0 1 2-2h13" />
        </svg>
      );
    case "arrow":
      return (
        <svg {...common}>
          <path d="M4 17V7a2 2 0 0 1 2-2h9" />
          <path d="M12 2l4 3-4 3" />
        </svg>
      );
    case "building":
      return (
        <svg {...common}>
          <rect x="4" y="3" width="16" height="18" rx="1.5" />
          <path d="M9 8h.01M15 8h.01M9 12h.01M15 12h.01M10 21v-4h4v4" />
        </svg>
      );
    case "hash":
      return (
        <svg {...common}>
          <path d="M5 9h14M5 15h14M9 4l-2 16M17 4l-2 16" />
        </svg>
      );
    case "kanban":
      return (
        <svg {...common}>
          <rect x="3" y="4" width="5" height="16" rx="1.5" />
          <rect x="9.5" y="4" width="5" height="10" rx="1.5" />
          <rect x="16" y="4" width="5" height="13" rx="1.5" />
        </svg>
      );
    case "checklist":
      return (
        <svg {...common}>
          <path d="M3 6l2 2 3-3M3 15l2 2 3-3" />
          <path d="M12 7h9M12 16h9" />
        </svg>
      );
    case "coins":
      return (
        <svg {...common}>
          <ellipse cx="12" cy="6" rx="8" ry="3" />
          <path d="M4 6v6c0 1.66 3.58 3 8 3s8-1.34 8-3V6" />
          <path d="M4 12v6c0 1.66 3.58 3 8 3s8-1.34 8-3v-6" />
        </svg>
      );
    case "gear":
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.7 1.7 0 0 0 .34 1.87l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.7 1.7 0 0 0-1.87-.34 1.7 1.7 0 0 0-1.03 1.56V21a2 2 0 1 1-4 0v-.09a1.7 1.7 0 0 0-1.03-1.56 1.7 1.7 0 0 0-1.87.34l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.7 1.7 0 0 0 4.6 15a1.7 1.7 0 0 0-1.56-1.03H3a2 2 0 1 1 0-4h.09A1.7 1.7 0 0 0 4.65 8.9a1.7 1.7 0 0 0-.34-1.87l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.7 1.7 0 0 0 1.87.34H9a1.7 1.7 0 0 0 1.03-1.56V3a2 2 0 1 1 4 0v.09c0 .68.4 1.3 1.03 1.56a1.7 1.7 0 0 0 1.87-.34l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.7 1.7 0 0 0-.34 1.87V9c.26.63.88 1.03 1.56 1.03H21a2 2 0 1 1 0 4h-.09A1.7 1.7 0 0 0 19.4 15z" />
        </svg>
      );
    case "beaker":
      return (
        <svg {...common}>
          <path d="M9 3h6M10 3v6l-5 9a2 2 0 0 0 2 3h10a2 2 0 0 0 2-3l-5-9V3" />
          <path d="M7 15h10" />
        </svg>
      );
    case "radar":
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" />
          <circle cx="12" cy="12" r="4.5" />
          <path d="M12 12l6-6M12 3v2M21 12h-2" />
        </svg>
      );
    case "plug":
      return (
        <svg {...common}>
          <path d="M9 3v5M15 3v5" />
          <path d="M6 8h12v3a6 6 0 0 1-12 0z" />
          <path d="M12 17v4" />
        </svg>
      );
    default:
      return <svg {...common} />;
  }
}
